import { useImpersonator, userHasFeature, useUserSession } from '@solinkcorp/authentication';

export const useAgentPermissions = () => {
  const user = useUserSession();
  const impersonator = useImpersonator();
  // Internal Solink (prod/dev) override: implicitly grants every capability below.
  // A Solink/Cloud super user keeps it while impersonating a customer that lacks it
  // (mirrors vd-server getPermissions). Only Solink/Cloud users can impersonate, so
  // the impersonator's flag is the right signal.
  const aiAgentsSuperUser =
    userHasFeature(user, 'aiAgentsSuperUser') ||
    (!!impersonator &&
      userHasFeature({ type: 'impersonator', profile: impersonator.profile }, 'aiAgentsSuperUser'));
  const aiAgentManage = userHasFeature(user, 'aiAgentManage');
  const aiAgentEnabled = userHasFeature(user, 'aiAgentEnabled');
  const aiAgentRead = userHasFeature(user, 'aiAgentRead');
  const aiDatasetsEnabled = userHasFeature(user, 'aiDatasetsEnabled');
  // Permissions migrated off the isSolink gate (SL-14217).
  const aiAgentManageTools = userHasFeature(user, 'aiAgentManageTools');
  const aiAgentManageContext = userHasFeature(user, 'aiAgentManageContext');
  const aiAgentViewMemories = userHasFeature(user, 'aiAgentViewMemories');
  const aiAgentCanCreateGlobalAgents = userHasFeature(user, 'aiAgentCanCreateGlobalAgents');
  const aiAgentViewTracing = userHasFeature(user, 'aiAgentViewTracing');
  const aiAgentRunPreview = userHasFeature(user, 'aiAgentRunPreview');
  const aiDatasetsTune = userHasFeature(user, 'aiDatasetsTune');
  const aiDatasetsManageFilterChains = userHasFeature(user, 'aiDatasetsManageFilterChains');
  const aiDatasetsManageSystemPrompt = userHasFeature(user, 'aiDatasetsManageSystemPrompt');
  const aiDatasetReadCosts = userHasFeature(user, 'aiDatasetReadCosts');
  return {
    // Raw super-user flag (impersonator-aware), exposed for visibility gating such
    // as cloud-scoped agents in the list.
    aiAgentsSuperUser,
    create: aiAgentsSuperUser || aiAgentManage || aiAgentEnabled,
    delete: aiAgentsSuperUser || aiAgentManage || aiAgentEnabled,
    preview: aiAgentsSuperUser || aiAgentManage || aiAgentEnabled,
    read: aiAgentsSuperUser || aiAgentRead || aiAgentManage || aiAgentEnabled,
    trigger: aiAgentsSuperUser || aiAgentManage || aiAgentEnabled,
    update: aiAgentsSuperUser || aiAgentManage || aiAgentEnabled,
    // Granular agent capabilities, formerly gated on isSolinkUser (SL-14217).
    manageTools: aiAgentsSuperUser || aiAgentManageTools,
    manageContext: aiAgentsSuperUser || aiAgentManageContext,
    viewMemories: aiAgentsSuperUser || aiAgentViewMemories,
    canCreateGlobalAgents: aiAgentsSuperUser || aiAgentCanCreateGlobalAgents,
    viewTracing: aiAgentsSuperUser || aiAgentViewTracing,
    runPreview: aiAgentsSuperUser || aiAgentRunPreview,
    // Dataset configuration capabilities (SL-14217).
    manageSystemPrompt: aiAgentsSuperUser || aiDatasetsManageSystemPrompt,
    manageFilterChains: aiAgentsSuperUser || aiDatasetsManageFilterChains,
    // Price calculator is internal-only; superuser override still applies (from develop).
    viewCalculator: aiAgentsSuperUser || ((aiAgentManage || aiAgentEnabled) && user.isSolinkUser),
    datasets: {
      read: aiAgentsSuperUser || aiDatasetsEnabled,
      create: aiAgentsSuperUser || aiDatasetsEnabled,
      update: aiAgentsSuperUser || aiDatasetsEnabled,
      delete: aiAgentsSuperUser || aiDatasetsEnabled,
      tune: aiAgentsSuperUser || aiDatasetsTune,
      restrictedIterations: false,
      // note, need to check both
      seeCosts: aiDatasetsEnabled && aiDatasetReadCosts,
    },
  };
};

export type AgentPermissions = ReturnType<typeof useAgentPermissions>;
